import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject, Signal, TemplateRef } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  pipe,
  tap,
  switchMap,
  filter,
  debounceTime,
  map,
  EMPTY,
  catchError,
  timer,
} from 'rxjs';
import { Subscription } from 'rxjs';
import {
  withReqState,
  setPropError,
  setPropLoading,
  setPropLoaded,
  setPropInit,
} from '@application/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';

import {
  EShortcutIdentifier,
  IShortcutItem,
  IUserSettings,
  languageCodeMap,
  shortcutsHiddenRoutes,
  SuperSearchPersonParams,
  SuperSearchPersonResponse,
  TUnsavedData,
} from './entities/shortcuts.model';
import { ShortcutsService } from './infra/shortcuts.service';
import {
  applyUiClientSettingsPatches,
  parseUiClientSettings,
} from './entities/ui-client-settings.model';
import { RouterStore } from '@route/data-access';
import { LanguageStore } from '@settings/language/data-access';
import { v4 as uuid } from 'uuid';
import {
  DataPerson,
  SearchResult,
} from '@shared/components/navbar/data-access';
import {
  SelfUserStore,
  uiClientSettingsPersistenceSubject,
} from '@domains/person/user/self/data';
let uiClientSettingsPersistenceSubscription: Subscription | null = null;

export interface IShortcutsStore {
  shortcuts: IShortcutItem[];
  selectPersonModal: {
    show: boolean;
    id: EShortcutIdentifier;
    title: string;
    subtitle?: string;
    action: (personId: string, personName?: string) => void;
    showNewCustomerOption?: boolean;
    showNewVisitorOption?: boolean;
    closeOnSelection?: boolean;
    contentAfterChoosing?: TemplateRef<any>;
    searchRegistrationType?: string;
  };
  selectConsumptionModal: {
    show: boolean;
    id: EShortcutIdentifier;
    title: string;
    searchRegistrationType?: string;
  };
  showSelectAssignorModal: boolean;
  unsavedData: Record<string, TUnsavedData>;
  editUnsavedDataId: string;
  superSearchPerson: {
    query: string;
    searching: boolean;
    results: SearchResult<DataPerson>;
    currentPage: number;
    totalPages: number;
    registrationType?: string;
  };
}

export const initialShortcutsStore: IShortcutsStore = {
  shortcuts: [],
  selectPersonModal: {
    show: false,
    id: null,
    title: null,
    subtitle: null,
    action: null,
    closeOnSelection: true,
    showNewCustomerOption: true,
    showNewVisitorOption: true,
    contentAfterChoosing: null,
    searchRegistrationType: 'Customer,Visitor,User',
  },
  selectConsumptionModal: {
    show: false,
    id: null,
    title: null,
    searchRegistrationType: 'Customer',
  },
  showSelectAssignorModal: false,
  unsavedData: {},
  editUnsavedDataId: null,
  superSearchPerson: {
    query: '',
    searching: false,
    results: {
      totalCount: 0,
      items: [],
      hasMoreItems: false,
    },
    currentPage: 1,
    totalPages: 1,
  },
};

export const ShortcutsStore = signalStore(
  { providedIn: 'root' },
  withState<IShortcutsStore>(initialShortcutsStore),
  withComputed((store) => ({
    unsavedDataItems: computed(() => {
      return Object.values(store.unsavedData());
    }),
  })),
  withMethods(
    (
      store,
      shortcutsService = inject(ShortcutsService),
      errorStore = inject(ErrorStore),
      languageStore = inject(LanguageStore),
      route = inject(RouterStore),
      userSelfStore = inject(SelfUserStore)
    ) => ({
      blankRequestState: () => {
        patchState(store, {
          ...setPropInit('getShortcuts', 'submitShortcuts'),
        });
      },
      getShortcuts: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, <NonNullable<object>>{
              ...setPropLoading('getShortcuts'),
            })
          ),
          switchMap(() =>
            shortcutsService.getShortcuts().pipe(
              tapResponse({
                next: (response) => {
                  const hasLocalChanges = userSelfStore.uiClientSettingsDirty();
                  if (hasLocalChanges) {
                    userSelfStore.setUserSettingsFromResponse(response, true);
                    userSelfStore.persistUiClientSettings();
                  } else {
                    userSelfStore.setUserSettingsFromResponse(response);
                  }
                  patchState(store, {
                    shortcuts: response?.Sidebar,
                    ...setPropLoaded('getShortcuts'),
                  });
                  const language = languageCodeMap[response?.LanguageCode];
                  if (language) {
                    languageStore.setLanguage(language);
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, <NonNullable<object>>{
                    ...setPropError('getShortcuts', error),
                  });
                },
              })
            )
          )
        )
      ),
      updateUserSettings: rxMethod<Partial<IUserSettings>>(
        pipe(
          tap(() =>
            patchState(store, { ...setPropLoading('updateUserSettings') })
          ),
          switchMap((payload) =>
            shortcutsService.updateUserSettings(payload).pipe(
              tapResponse({
                next: (userSettings) => {
                  userSelfStore.setUserSettingsFromResponse(userSettings);
                  patchState(store, {
                    ...setPropLoaded('updateUserSettings'),
                  });
                  const language = languageCodeMap[userSettings?.LanguageCode];
                  if (language) {
                    languageStore.setLanguage(language);
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, {
                    ...setPropError('updateUserSettings', error),
                  });
                },
              })
            )
          )
        )
      ),
      updateShortcuts: rxMethod<IShortcutItem[]>(
        pipe(
          tap(() =>
            patchState(store, { ...setPropLoading('submitShortcuts') })
          ),
          switchMap((payload) =>
            shortcutsService.createShortcuts(payload).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    shortcuts: response,
                    ...setPropLoaded('submitShortcuts'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, {
                    ...setPropError('submitShortcuts', error),
                  });
                },
              })
            )
          )
        )
      ),
      isShortcutsHidden(): Signal<boolean> {
        return computed(() => {
          const routeUrl = route.getCurrentRoute();
          return !!shortcutsHiddenRoutes.find((item) =>
            routeUrl.includes(item)
          );
        });
      },
      openSelectPersonModal: (
        params: Partial<IShortcutsStore['selectPersonModal']>
      ) => {
        patchState(store, {
          selectPersonModal: {
            ...initialShortcutsStore.selectPersonModal,
            ...params,
            show: true,
          },
        });
      },
      openSelectConsumptionModal: (
        params: Partial<IShortcutsStore['selectConsumptionModal']>
      ) => {
        patchState(store, {
          selectConsumptionModal: {
            ...initialShortcutsStore.selectConsumptionModal,
            ...params,
            show: true,
          },
        });
      },
      closeSelectPersonModal: () => {
        patchState(store, {
          selectPersonModal: initialShortcutsStore.selectPersonModal,
        });
      },
      closeSelectConsumptionModal: () => {
        patchState(store, {
          selectConsumptionModal: initialShortcutsStore.selectConsumptionModal,
        });
      },
      openSelectAssignorModal: () => {
        patchState(store, { showSelectAssignorModal: true });
      },
      closeSelectAssignorModal: () => {
        patchState(store, { showSelectAssignorModal: false });
      },

      addToUnsavedData: (data: TUnsavedData) => {
        const id: string = data.id || uuid();
        patchState(store, {
          editUnsavedDataId: null,
          unsavedData: { ...store.unsavedData(), [id]: { ...data, id } },
        });
      },

      removeFromUnsavedData: (id: string) => {
        const unsavedData = { ...store.unsavedData() };
        delete unsavedData[id];
        patchState(store, { unsavedData, editUnsavedDataId: null });
      },

      setEditUnsavedDataId: (editUnsavedDataId: string) => {
        patchState(store, { editUnsavedDataId });
      },

      clearSuperSearchPerson: () => {
        patchState(store, {
          superSearchPerson: { ...initialShortcutsStore.superSearchPerson },
        });
      },

      setSuperSearchRegistrationType: (registrationType: string) => {
        patchState(store, {
          superSearchPerson: { ...store.superSearchPerson(), registrationType },
        });
      },

      getSuperSearchPerson: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              superSearchPerson: {
                ...store.superSearchPerson(),
                searching: true,
              },
              ...setPropLoading('getSuperSearchPerson'),
            })
          ),
          debounceTime(300),
          // Ver `getSuperSearch` no navbar store: o serviço devolve 400 a um `q`
          // vazio *ou* só com espaços, e o antigo `length >= 1` deixava `"  "`
          // passar. Normalizar aqui cobre os chamadores que não filtram nada.
          map((query) => query?.trim() ?? ''),
          tap((query) => {
            if (!query) {
              patchState(store, {
                superSearchPerson: {
                  ...store.superSearchPerson(),
                  searching: false,
                },
                ...setPropInit('getSuperSearchPerson'),
              });
            }
          }),
          filter((query) => query.length >= 1),
          switchMap((query) => {
            patchState(store, {
              superSearchPerson: { ...store.superSearchPerson(), query },
            });

            const params: SuperSearchPersonParams = {
              q: query,
              page: 1,
            };

            if (store.superSearchPerson()?.registrationType)
              params.registrationType =
                store.superSearchPerson()?.registrationType;

            return shortcutsService.getSuperSearchPerson(params);
          }),
          tapResponse({
            next: (response: SuperSearchPersonResponse) => {
              patchState(store, {
                superSearchPerson: {
                  ...store.superSearchPerson(),
                  searching: false,
                  results: {
                    totalCount: response.total ?? 0,
                    items: response.results ?? [],
                    hasMoreItems: response?.page < response?.pages,
                  },
                  currentPage: response?.page ?? 1,
                  totalPages: response?.pages ?? 1,
                },
                ...setPropLoaded('getSuperSearchPerson'),
              });
            },
            error: (error) => {
              patchState(store, {
                superSearchPerson: {
                  ...store.superSearchPerson(),
                  searching: false,
                },
                ...setPropError('getSuperSearchPerson', error),
              });
            },
          })
        )
      ),
      loadMoreSuperSearchPersonResults: rxMethod<void>(
        pipe(
          // Idem: sem termo guardado não há página seguinte para pedir.
          filter(() => !!store.superSearchPerson()?.query?.trim()),
          tap(() =>
            patchState(store, {
              ...setPropLoading('loadMoreSuperSearchPersonResults'),
            })
          ),
          switchMap(() => {
            const currentSuperSearch = store.superSearchPerson();
            const nextPage = currentSuperSearch.currentPage + 1;

            const params: SuperSearchPersonParams = {
              q: store?.superSearchPerson()?.query,
              page: nextPage,
            };

            if (store.superSearchPerson()?.registrationType)
              params.registrationType =
                store.superSearchPerson()?.registrationType;

            return shortcutsService.getSuperSearchPerson(params);
          }),
          tapResponse({
            next: (response: SuperSearchPersonResponse) => {
              const currentItems = store.superSearchPerson().results.items;

              patchState(store, {
                superSearchPerson: {
                  ...store.superSearchPerson(),
                  results: {
                    totalCount: response.total ?? 0,
                    items: currentItems.concat(response.results ?? []),
                    hasMoreItems: response?.page < response?.pages,
                  },
                  currentPage: response?.page ?? 1,
                  totalPages: response?.pages ?? 1,
                },
                ...setPropLoaded('loadMoreSuperSearchPersonResults'),
              });
            },
            error: (error) =>
              patchState(store, {
                ...setPropError('loadMoreSuperSearchPersonResults', error),
              }),
          })
        )
      ),

    })
  ),
  withHooks({
    onInit: () => {
      if (uiClientSettingsPersistenceSubscription) {
        return;
      }
      const shortcutsService = inject(ShortcutsService);
      const userSelfStore = inject(SelfUserStore);
      const errorStore = inject(ErrorStore);
      uiClientSettingsPersistenceSubscription = uiClientSettingsPersistenceSubject
        .pipe(
          switchMap(() =>
            timer(100).pipe(
              switchMap(() => {
                const userSettingsId = userSelfStore.userSettings()?.UserSettingsId;
                if (!userSettingsId || !userSelfStore.uiClientSettingsDirty()) {
                  return EMPTY;
                }
                const pending = userSelfStore.pendingUiClientSettingsPatches();
                // The whole blob is written, so it is built over what the server
                // holds now and not over this tab's copy: a copy loaded before
                // another tab (or a newer client) changed something would write
                // the old value back and switch it off for everyone.
                return shortcutsService.getShortcuts().pipe(
                  switchMap((current) => {
                    const toSave = applyUiClientSettingsPatches(
                      parseUiClientSettings(current?.UiClientSettings),
                      pending
                    );
                    return shortcutsService
                      .updateUiClientSettingsAsync(userSettingsId, toSave)
                      .pipe(
                        tap((updatedSettings) =>
                          userSelfStore.settleUiClientSettingsSave(
                            {
                              ...updatedSettings,
                              UserSettingsId:
                                updatedSettings?.UserSettingsId ?? userSettingsId,
                              UiClientSettings: toSave,
                            },
                            pending.length
                          )
                        )
                      );
                  }),
                  catchError((error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    return EMPTY;
                  })
                );
              })
            )
          )
        )
        .subscribe();
    },
    onDestroy: () => {
      if (uiClientSettingsPersistenceSubscription) {
        uiClientSettingsPersistenceSubscription.unsubscribe();
        uiClientSettingsPersistenceSubscription = null;
      }
    },
  }),
  withReqState(
    'getShortcuts',
    'submitShortcuts',
    'updateUserSettings',
    'getSuperSearchPerson',
    'loadMoreSuperSearchPersonResults'
  )
);
